import { Injectable, OnDestroy } from "@angular/core";
import { Observable, Subject, timer, EMPTY, of, throwError, defer } from "rxjs";
import { HttpClient } from "@angular/common/http";
import {
    expand,
    map,
    catchError,
    takeUntil,
    shareReplay,
    switchMap,
    finalize,
} from "rxjs/operators";
import {
    JOB_POLLING_INTERVAL_INITIAL_MS,
    JOB_POLLING_INTERVAL_MULTIPLIER,
    JOB_POLLING_INTERVAL_MAX_MS,
    JOB_POLLING_MAX_TOTAL_TIME_MS,
} from "../shared/globals";

export type JobPollMessage = string | { [key: string]: unknown } | unknown[];

export interface JobPollResponse {
    status: boolean;
    message?: JobPollMessage;
    processing?: boolean;
    filename?: string;
    data?: { [key: string]: any };
    error?: any;
}

export const getMessageString = (message: unknown): string => {
    if (!message) return "";
    if (typeof message === "string") return message;
    if (Array.isArray(message)) {
        return message.map(getMessageString).filter(Boolean).join("\n");
    }
    if (typeof message === "object") {
        const messageObject = message as { [key: string]: unknown };
        const nestedMessage = getMessageString(
            messageObject.message ??
                messageObject.detail ??
                messageObject.title ??
                messageObject.error ??
                messageObject.errors,
        );
        if (nestedMessage) return nestedMessage;
        try {
            return JSON.stringify(message);
        } catch {
            return String(message);
        }
    }
    return String(message);
};

export const getMessageStringFromJobPollResponse = (jobResponse: JobPollResponse): string => {
    return getMessageString(jobResponse.message ?? jobResponse.error);
};

@Injectable({ providedIn: "root" })
export class BackgroundJobService implements OnDestroy {
    private activePolls = new Map<
        string,
        { observable: Observable<JobPollResponse>; cancel$: Subject<void> }
    >();
    private jobPollEndpoint = "/api/job-status/";
    private readonly destroy$ = new Subject<void>();

    constructor(private http: HttpClient) {}

    /**
     * Start polling a job with exponential back-off.
     *
     * @param jobId The backend job identifier.
     * @returns Observable emitting JobPollResponse updates until completion, timeout or cancellation.
     */
    createJobPoll(jobId: string): Observable<JobPollResponse> {
        // Cancel any existing poll for this job
        this.cancelPoll(jobId);

        const cancel$ = new Subject<void>();

        const poll$ = this.pollWithProgressiveBackoff(jobId).pipe(
            takeUntil(cancel$),
            takeUntil(this.destroy$),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        this.activePolls.set(jobId, { observable: poll$, cancel$ });
        return poll$;
    }

    /** Core loop with exponential back-off and a hard overall timeout. */
    private pollWithProgressiveBackoff(jobId: string): Observable<JobPollResponse> {
        let currentInterval = JOB_POLLING_INTERVAL_INITIAL_MS;

        // Hard timeout that aborts the whole poll regardless of intermediate emissions
        const hardTimeout$ = timer(JOB_POLLING_MAX_TOTAL_TIME_MS).pipe(
            switchMap(() =>
                throwError(() => ({
                    status: false,
                    processing: false,
                    message: "Job polling timed out.",
                })),
            ),
        );

        return this.pollJob(jobId).pipe(
            expand((response: JobPollResponse) => {
                // Stop polling if the job is finished (success or failure)
                if (response.status || !response.processing) {
                    return EMPTY;
                }

                // Exponential back-off capped & rounded-up to the nearest second
                const nextInterval = Math.min(
                    Math.ceil((currentInterval * JOB_POLLING_INTERVAL_MULTIPLIER) / 1000) * 1000,
                    JOB_POLLING_INTERVAL_MAX_MS,
                );
                currentInterval = nextInterval;

                return timer(currentInterval).pipe(switchMap(() => this.pollJob(jobId)));
            }),
            takeUntil(hardTimeout$),
            finalize(() => this.activePolls.delete(jobId)),
        );
    }

    /** Single GET request plus normalisation & soft error handling. */
    private pollJob(jobId: string): Observable<JobPollResponse> {
        return this.http.get<JobPollResponse>(`${this.jobPollEndpoint}${jobId}`).pipe(
            map((resp) => ({
                status: resp.status || false,
                processing: resp.processing !== false && !resp.status,
                message: resp.message || (resp.status ? "Job completed successfully" : undefined),
                data: resp.data,
                filename: resp.filename,
            })),
            catchError((err) => {
                console.error("Error polling job:", err);
                const rawMessage = err.error?.error ?? err.error?.errors ?? err.error?.message ?? err.message;
                const message = getMessageString(rawMessage) || "An error occurred while polling the job";
                return of({
                    status: false,
                    processing: false,
                    message: message,
                });
            }),
        );
    }

    private cancelPoll(jobId: string): void {
        const poll = this.activePolls.get(jobId);
        if (poll) {
            poll.cancel$.next();
            poll.cancel$.complete();
            this.activePolls.delete(jobId);
        }
    }

    cancelAllPolls(): void {
        this.activePolls.forEach((poll) => {
            poll.cancel$.next();
            poll.cancel$.complete();
        });
        this.activePolls.clear();
    }

    ngOnDestroy(): void {
        this.cancelAllPolls();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
